import { GridColDef } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';

import { Application } from '@libs/models';
import { formatPhone } from '@libs/utils';
import { routes } from '@carcareers/utils';
import { ApplicationStatusChip, DataGridLink } from '@carcareers/components';

export const createApplicationIndexColumns = (isAdmin: boolean): GridColDef[] => {
  return [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'jobseekerName',
      headerName: 'Candidate Name',
      width: 200,
      renderCell: (params) => {
        const jobseekerId = params.row.jobseeker?.id.toString();
        const name = `${params.row.jobseeker?.user?.firstName} ${params.row.jobseeker?.user?.lastName}`;
        const adminPath = `/${routes.ADMIN_HOME}/${routes.ADMIN_JOBSEEKERS_VIEW.replace(':id', jobseekerId)}`;
        const employerPath = `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_CANDIDATES_VIEW.replace(':id', jobseekerId)}`;
        const path = isAdmin ? adminPath : employerPath;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DataGridLink content={name} link={path} />
            {Boolean(params.row.jobseeker?.isExternalApply) && (
              <Typography variant="body2" color="text.secondary">
                (external)
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      valueGetter: (_, row) => row.jobseeker?.user?.email || 'N/A',
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      valueGetter: (_, row) => formatPhone(row.jobseeker?.user?.phone) || 'N/A',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const application = params.row as Application;
        return (
          <ApplicationStatusChip
            status={application.status}
            customStatus={application.customStatus}
            statusColor={application.statusColor}
          />
        );
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
      renderCell: (params) => params.row.source,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      renderCell: (params) => {
        return <Typography>{params.row.notes}</Typography>;
      },
    },
    {
      field: 'jobTitle',
      headerName: 'Applied To',
      width: 250,
      renderCell: (params) => (
        <DataGridLink
          content={params.row.post?.job?.title || 'N/A'}
          link={`/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_POSTS_VIEW.replace(
            ':id',
            params.row?.post?.id.toString(),
          )}`}
        />
      ),
    },
    {
      field: 'rooftop',
      headerName: 'Rooftop',
      width: 200,
      renderCell: (params) => (
        <DataGridLink
          content={params.row.post?.job?.rooftop?.name || 'N/A'}
          link={`/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_ROOFTOPS_VIEW.replace(
            ':id',
            params.row?.post?.job?.rooftop?.id.toString(),
          )}`}
        />
      ),
    },
    {
      field: 'appliedAt',
      headerName: 'Applied On',
      width: 150,
      valueGetter: (_, row) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })
          : 'N/A',
    },
  ];
};

export const APPLICATION_INDEX_COLUMNS: GridColDef[] = [];

export const APPLICATION_INDEX_COLUMN_INITIAL_STATE = {
  id: false,
  jobseekerName: true,
  email: true,
  phone: true,
  status: true,
  jobTitle: true,
  rooftop: true,
  appliedAt: true,
  notes: false,
  source: true,
};
